import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, CheckCircle } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthenticatedHomePath } from '../../utils/collaboratorPermissions';
import './CompanySelector.css';

const CompanySelector = () => {
  const navigate = useNavigate();
  const { userData, currentUser } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSelectCompany = useCallback(async (companyId) => {
    try {
      setLoading(true);

      // Atualiza currentCompanyId no Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        currentCompanyId: companyId,
        lastCompanySelection: new Date()
      });

      window.location.href = getAuthenticatedHomePath({
        ...userData,
        currentCompanyId: companyId
      });
      
    } catch (error) {
      console.error('Erro ao selecionar empresa:', error);
      alert('Erro ao selecionar empresa. Tente novamente.');
      setLoading(false);
    }
  }, [currentUser, userData]);

  useEffect(() => {
    if (userData?.role !== 'collaborator' || !userData?.companies || userData.companies.length === 0) {
      navigate(getAuthenticatedHomePath(userData));
      return;
    }

    if (userData.companies.length === 1) {
      handleSelectCompany(userData.companies[0].companyId);
    }
  }, [userData, navigate, handleSelectCompany]);

  if (!userData?.companies || userData.companies.length === 0) {
    return null;
  }

  // Filtrar apenas empresas ativas
  const activeCompanies = userData.companies.filter(c => c.active !== false);

  if (activeCompanies.length === 0) {
    return (
      <div className="company-selector-container">
        <div className="company-selector-card">
          <div className="company-selector-header">
            <img src="/logo.png" alt="SDC Turismo" className="company-selector-logo" />
            <Building2 size={48} />
            <h1>Nenhuma Empresa Disponível</h1>
            <p>Você não possui acesso a nenhuma empresa no momento.</p>
            <p className="help-text">Entre em contato com o administrador da sua empresa.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-selector-container">
      <div className="company-selector-card">
        <div className="company-selector-header">
          <img src="/logo.png" alt="SDC Turismo" className="company-selector-logo" />
          <Building2 size={48} />
          <h1>Selecione uma Empresa</h1>
          <p>Você está vinculado a {activeCompanies.length} {activeCompanies.length === 1 ? 'empresa' : 'empresas'}</p>
        </div>

        <div className="companies-list">
          {activeCompanies.map((company) => (
            <button
              key={company.companyId}
              className={`company-item ${selectedCompany === company.companyId ? 'selected' : ''}`}
              onClick={() => setSelectedCompany(company.companyId)}
              disabled={loading}
            >
              <div className="company-info">
                <div className="company-icon">
                  <Building2 size={24} />
                </div>
                <div className="company-details">
                  <h3>{company.companyName}</h3>
                  <p>{company.role === 'collaborator' ? 'Colaborador' : company.role}</p>
                  {company.addedAt && (
                    <span className="company-date">
                      Desde {new Date(company.addedAt.toDate ? company.addedAt.toDate() : company.addedAt).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </div>
              </div>
              
              {selectedCompany === company.companyId ? (
                <CheckCircle size={24} className="check-icon" />
              ) : (
                <ChevronRight size={24} className="arrow-icon" />
              )}
            </button>
          ))}
        </div>

        {selectedCompany && (
          <div className="company-selector-footer">
            <button
              className="btn-continue"
              onClick={() => handleSelectCompany(selectedCompany)}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Carregando...
                </>
              ) : (
                <>
                  Continuar
                  <ChevronRight size={20} />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanySelector;
